export interface NpcMoodProfile {
  baselineValence: number;
  baselineArousal: number;
  reactivity: number;
  decayHalfLife: number;
}

export interface InternalEmbodimentState {
  processingLoad: number;
  confidence: number;
  uncertainty: number;
}

export type MoodInfluenceSource =
  | 'conversation'
  | 'time_decay'
  | 'budget_change'
  | 'task_feedback'
  | 'tool_breakdown';

export interface MoodInfluence {
  source: MoodInfluenceSource;
  valenceDelta: number;
  arousalDelta: number;
  timestampMs: number;
  sourceEntityId?: string;
}

export interface NpcMoodState {
  valence: number;
  arousal: number;
  embodiment: InternalEmbodimentState;
  lastUpdatedMs: number;
  recentInfluences: MoodInfluence[];
}

export type MoodOverridePreset = 'calm' | 'angry' | 'anxious' | 'curious';

export type MoodOverrideTarget =
  | { scope: 'participant'; npcId: string }
  | { scope: 'all_participants' };

export interface MoodOverride {
  id: string;
  sessionType: 'council' | 'simulation';
  target: MoodOverrideTarget;
  preset: MoodOverridePreset | null;
  valence: number | null;
  arousal: number | null;
  strength: number;
  active: boolean;
  createdBy: string;
  createdAtMs: number;
  updatedAtMs: number;
  clearedAtMs?: number | null;
}

export interface EffectiveMoodState {
  valence: number;
  arousal: number;
  embodiment: InternalEmbodimentState;
  overridePreset?: MoodOverridePreset | null;
  overrideStrength?: number;
}

export const DEFAULT_MOOD_PROFILE: NpcMoodProfile = {
  baselineValence: 0,
  baselineArousal: 0,
  reactivity: 0.5,
  decayHalfLife: 6,
};

export const DEFAULT_EMBODIMENT: InternalEmbodimentState = {
  processingLoad: 0.3,
  confidence: 0.7,
  uncertainty: 0.3,
};

export const DEFAULT_MOOD_OVERRIDE_STRENGTH = 0.7;

export const MOOD_OVERRIDE_PRESETS: Record<
  MoodOverridePreset,
  Pick<EffectiveMoodState, 'valence' | 'arousal'>
> = {
  calm: { valence: 0.45, arousal: -0.55 },
  angry: { valence: -0.75, arousal: 0.8 },
  anxious: { valence: -0.6, arousal: 0.55 },
  curious: { valence: 0.55, arousal: 0.35 },
};

export const clampMoodCoordinate = (value: number): number =>
  Math.max(-1, Math.min(1, Number.isFinite(value) ? value : 0));

export const clampZeroToOne = (value: number): number =>
  Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));

export const clampMoodOverrideStrength = (value: number): number =>
  Math.max(0, Math.min(1, Number.isFinite(value) ? value : DEFAULT_MOOD_OVERRIDE_STRENGTH));

export const clampMoodHalfLife = (value: number): number =>
  Math.max(3, Math.min(24, Number.isFinite(value) ? value : DEFAULT_MOOD_PROFILE.decayHalfLife));

export const normalizeMoodProfile = (
  value: Partial<NpcMoodProfile> | null | undefined,
): NpcMoodProfile => ({
  baselineValence: clampMoodCoordinate(
    value?.baselineValence ?? DEFAULT_MOOD_PROFILE.baselineValence,
  ),
  baselineArousal: clampMoodCoordinate(
    value?.baselineArousal ?? DEFAULT_MOOD_PROFILE.baselineArousal,
  ),
  reactivity: clampZeroToOne(value?.reactivity ?? DEFAULT_MOOD_PROFILE.reactivity),
  decayHalfLife: clampMoodHalfLife(value?.decayHalfLife ?? DEFAULT_MOOD_PROFILE.decayHalfLife),
});

export const normalizeEmbodiment = (
  value: Partial<InternalEmbodimentState> | null | undefined,
): InternalEmbodimentState => ({
  processingLoad: clampZeroToOne(value?.processingLoad ?? DEFAULT_EMBODIMENT.processingLoad),
  confidence: clampZeroToOne(value?.confidence ?? DEFAULT_EMBODIMENT.confidence),
  uncertainty: clampZeroToOne(value?.uncertainty ?? DEFAULT_EMBODIMENT.uncertainty),
});

const toMoodInfluenceSource = (value: unknown): MoodInfluenceSource =>
  value === 'time_decay' ||
  value === 'budget_change' ||
  value === 'task_feedback' ||
  value === 'tool_breakdown'
    ? value
    : 'conversation';

export const normalizeMoodState = (
  value: Partial<NpcMoodState> | null | undefined,
  fallbackProfile?: Partial<NpcMoodProfile> | null,
): NpcMoodState => {
  const profile = normalizeMoodProfile(fallbackProfile);
  return {
    valence: clampMoodCoordinate(value?.valence ?? profile.baselineValence),
    arousal: clampMoodCoordinate(value?.arousal ?? profile.baselineArousal),
    embodiment: normalizeEmbodiment(value?.embodiment),
    lastUpdatedMs:
      typeof value?.lastUpdatedMs === 'number' && Number.isFinite(value.lastUpdatedMs)
        ? value.lastUpdatedMs
        : Date.now(),
    recentInfluences: Array.isArray(value?.recentInfluences)
      ? value.recentInfluences
          .map((entry) => ({
            source: toMoodInfluenceSource(entry?.source),
            valenceDelta: clampMoodCoordinate(
              typeof entry?.valenceDelta === 'number' ? entry.valenceDelta : 0,
            ),
            arousalDelta: clampMoodCoordinate(
              typeof entry?.arousalDelta === 'number' ? entry.arousalDelta : 0,
            ),
            timestampMs:
              typeof entry?.timestampMs === 'number' && Number.isFinite(entry.timestampMs)
                ? entry.timestampMs
                : Date.now(),
            ...(typeof entry?.sourceEntityId === 'string' && entry.sourceEntityId.trim()
              ? { sourceEntityId: entry.sourceEntityId.trim() }
              : {}),
          }))
          .slice(-10)
      : [],
  };
};

export const resolveMoodOverrideDocumentId = (target: MoodOverrideTarget): string =>
  target.scope === 'all_participants' ? 'all_participants' : `participant:${target.npcId}`;

export const resolveMoodOverrideForNpc = (
  overrides: MoodOverride[],
  npcId: string,
): MoodOverride | null =>
  overrides.find(
    (override) =>
      override.active && override.target.scope === 'participant' && override.target.npcId === npcId,
  ) ??
  overrides.find((override) => override.active && override.target.scope === 'all_participants') ??
  null;

export interface SetCouncilMoodOverrideRequest {
  councilId: string;
  target: MoodOverrideTarget;
  preset?: MoodOverridePreset | null;
  valence?: number | null;
  arousal?: number | null;
  strength?: number | null;
}

export interface ClearCouncilMoodOverrideRequest {
  councilId: string;
  target: MoodOverrideTarget;
}

export interface SetSimulationMoodOverrideRequest {
  simulationId: string;
  target: MoodOverrideTarget;
  preset?: MoodOverridePreset | null;
  valence?: number | null;
  arousal?: number | null;
  strength?: number | null;
}

export interface ClearSimulationMoodOverrideRequest {
  simulationId: string;
  target: MoodOverrideTarget;
}
